/**
 * Data Anomaly Watcher — a top-level admin tab. A "watcher" is a scheduled job
 * (kind "anomaly") that periodically runs a set of CHECKS against a database and
 * alerts (via the job's notify target) when any check trips. Each check is a
 * metric SQL → single number, compared by an operator to a threshold; relative
 * operators (drop%/rise%/change%) compare to the previous run's value.
 *
 * This panel is the status board + editor; scheduling, run history and alerting
 * are the existing jobs scheduler (managed through the same /api/tools/jobs API).
 * Props: { active }.
 */
import { createSignal, createResource, For, Show } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { api } from '../lib/api.js';
import Modal from '../components/Modal.jsx';
import { listJobs, createJob, updateJob, deleteJob, runJobNow } from '../lib/jobs.js';
import { testAnomalyCheck, OPERATORS, isRelativeOp, TEMPLATES } from '../lib/anomaly.js';
import { relTime } from '../lib/format.js';
import { toast } from '../lib/toast.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

async function fetchDbs(id) {
  if (!id) return [];
  try {
    const { databases } = await api.get(`/api/connections/${id}/databases`);
    return (databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n));
  } catch { return []; }
}

// ── schedule Spec ↔ UI ───────────────────────────────────────────────────────
const timeToAtMin = (t) => { const [h, m] = String(t || '09:00').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const atMinToTime = (n) => `${String(Math.floor((n || 0) / 60)).padStart(2, '0')}:${String((n || 0) % 60).padStart(2, '0')}`;
const defaultSchedule = () => ({ kind: 'interval', everyN: 6, everyUnit: 'hour', time: '09:00', days: [1], dom: 1 });
const toSpec = (s) => {
  if (s.kind === 'interval') return { kind: 'interval', everyMin: (Number(s.everyN) || 1) * (s.everyUnit === 'hour' ? 60 : 1) };
  if (s.kind === 'daily') return { kind: 'daily', atMin: timeToAtMin(s.time) };
  if (s.kind === 'weekly') return { kind: 'weekly', atMin: timeToAtMin(s.time), days: [...s.days].sort((a, b) => a - b) };
  return { kind: 'monthly', atMin: timeToAtMin(s.time), dom: Number(s.dom) || 1 };
};
const fromSpec = (spec) => {
  const base = defaultSchedule();
  if (!spec || !spec.kind) return base;
  if (spec.kind === 'interval') {
    const m = spec.everyMin || 60;
    return { ...base, kind: 'interval', everyN: m % 60 === 0 ? m / 60 : m, everyUnit: m % 60 === 0 ? 'hour' : 'min' };
  }
  base.kind = spec.kind;
  base.time = atMinToTime(spec.atMin || 540);
  if (spec.kind === 'weekly') base.days = spec.days?.length ? spec.days : [1];
  if (spec.kind === 'monthly') base.dom = spec.dom || 1;
  return base;
};

let checkSeq = 0;
const newCheck = () => ({ id: `c${++checkSeq}`, name: '', template: 'rowcount', table: '', column: '', lo: 0, hi: 0, sql: '', operator: 'drop%', threshold: 20, testVal: null, testErr: '' });

export default function AnomalyWatcherPanel() {
  const [data, { refetch }] = createResource(async () => (await listJobs()).filter((j) => j.kind === 'anomaly'));
  const [conns] = createResource(async () => (await api.get('/api/connections')).connections ?? []);
  const [targets] = createResource(async () => {
    try { return (await api.get('/api/admin/notification-targets')).targets ?? []; } catch { return []; }
  });
  const connName = (id) => conns()?.find((c) => String(c.id) === String(id))?.name || `#${id}`;

  const [ed, setEd] = createStore({ open: false, isNew: true, id: null, saving: false });
  const [input, setInput] = createStore({});
  const [dbList, setDbList] = createSignal(null);

  const openNew = async () => {
    setInput({
      name: '', connectionId: '', database: '',
      schedule: defaultSchedule(), notifyTargetId: 0, notifyOn: 'failure', enabled: true,
      checks: [newCheck()],
    });
    setDbList(null);
    setEd({ open: true, isNew: true, id: null, saving: false });
  };
  const openEdit = async (j) => {
    const cfg = j.config || {};
    setInput({
      name: j.name, connectionId: String(cfg.connectionId || ''), database: cfg.database || '',
      schedule: fromSpec(j.schedule), notifyTargetId: j.notifyTargetId || 0, notifyOn: j.notifyOn || 'failure',
      enabled: j.enabled,
      checks: (cfg.checks || []).map((c) => ({ ...newCheck(), id: c.id || `c${++checkSeq}`, name: c.name, template: 'custom', sql: c.sql, operator: c.operator, threshold: c.threshold })),
    });
    setDbList(cfg.connectionId ? await fetchDbs(cfg.connectionId) : null);
    setEd({ open: true, isNew: false, id: j.id, saving: false });
  };
  const closeEd = () => setEd('open', false);

  const onConn = async (id) => { setInput('connectionId', id); setInput('database', ''); setDbList(id ? await fetchDbs(id) : null); };

  // ── check editing ──────────────────────────────────────────────────────────
  const regen = (i) => {
    setInput('checks', i, produce((c) => {
      if (c.template === 'custom') return;
      const tpl = TEMPLATES.find((t) => t.id === c.template);
      if (!tpl?.build) return;
      const built = tpl.build(c);
      c.sql = built.sql; c.operator = built.operator; c.threshold = built.threshold;
    }));
  };
  const setCheck = (i, key, val) => { setInput('checks', i, key, val); if (['template', 'table', 'column', 'lo', 'hi'].includes(key)) regen(i); };
  const addCheck = () => setInput('checks', (cs) => [...cs, newCheck()]);
  const removeCheck = (i) => setInput('checks', (cs) => cs.filter((_, idx) => idx !== i));
  const testCheck = async (i) => {
    const c = input.checks[i];
    setInput('checks', i, produce((x) => { x.testVal = null; x.testErr = ''; }));
    try {
      const r = await testAnomalyCheck(input.connectionId, input.database, c.sql);
      if (r.ok) setInput('checks', i, 'testVal', r.value);
      else setInput('checks', i, 'testErr', r.error);
    } catch (e) { setInput('checks', i, 'testErr', e.message); }
  };

  const tplNeeds = (i, field) => TEMPLATES.find((t) => t.id === input.checks[i].template)?.needs.includes(field);

  const save = async () => {
    if (!input.name.trim()) { toast.error('Name the watcher.'); return; }
    if (!input.connectionId) { toast.error('Pick a connection.'); return; }
    const checks = input.checks.filter((c) => c.sql.trim());
    if (!checks.length) { toast.error('Add at least one check with a metric SQL.'); return; }
    setEd('saving', true);
    const job = {
      name: input.name.trim(), kind: 'anomaly', enabled: input.enabled,
      config: {
        connectionId: Number(input.connectionId), database: input.database,
        checks: checks.map((c) => ({ id: c.id, name: c.name || 'check', sql: c.sql.trim(), operator: c.operator, threshold: Number(c.threshold) || 0 })),
      },
      schedule: toSpec(input.schedule),
      notifyTargetId: Number(input.notifyTargetId) || 0, notifyOn: input.notifyOn,
    };
    try {
      if (ed.isNew) await createJob(job); else await updateJob(ed.id, job);
      toast.success(ed.isNew ? 'Watcher created.' : 'Watcher saved.');
      closeEd(); refetch();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setEd('saving', false); }
  };

  const runNow = async (j) => { try { await runJobNow(j.id); toast.info(`Running "${j.name}"…`); setTimeout(refetch, 1500); } catch (e) { toast.error(e.message); } };
  const remove = async (j) => { if (!confirm(`Delete watcher "${j.name}"? Its history is removed too.`)) return; try { await deleteJob(j.id); refetch(); } catch (e) { toast.error(e.message); } };
  const toggleEnabled = async (j) => {
    try { await updateJob(j.id, { name: j.name, kind: 'anomaly', enabled: !j.enabled, config: j.config, schedule: j.schedule, notifyTargetId: j.notifyTargetId, notifyOn: j.notifyOn }); refetch(); }
    catch (e) { toast.error(e.message); }
  };

  const statusOf = (j) => {
    const lr = j.latestRun;
    if (!lr) return { dot: 'fhealth-dot-idle', text: 'not run yet' };
    if (lr.status === 'ok') return { dot: 'fhealth-dot-ok', text: 'all clear' };
    return { dot: 'aw-dot-alert', text: lr.summary || 'anomaly' };
  };

  return (
    <div class="fhealth-panel aw-panel">
      <div class="fhealth-head">
        <div>
          <h2>Data Anomaly Watcher</h2>
          <p class="muted">Watch a database on a schedule — row-count drops, NULL spikes, staleness, duplicates, out-of-range — and alert when a check trips.</p>
        </div>
        <div class="fhealth-actions">
          <button class="btn" onClick={openNew}>＋ New watcher</button>
          <button class="btn btn-ghost" disabled={data.loading} onClick={refetch}>{data.loading ? '…' : '⟳'}</button>
        </div>
      </div>

      <Show when={data() && !data().length}>
        <div class="muted" style="padding:20px;font-size:13px;">No watchers yet. Create one to monitor a table for sudden drops, NULL spikes, staleness, or duplicates.</div>
      </Show>

      <Show when={data() && data().length}>
        <table class="jobs-table fhealth-table">
          <thead>
            <tr><th></th><th>Watcher</th><th>Target</th><th>Checks</th><th>Schedule</th><th>Last result</th><th></th></tr>
          </thead>
          <tbody>
            <For each={data()}>
              {(j) => {
                const s = statusOf(j);
                return (
                  <tr class="fhealth-row" classList={{ 'aw-disabled': !j.enabled }}>
                    <td><span class={`fhealth-dot ${s.dot}`} /></td>
                    <td><span class="fhealth-name">{j.name}</span><Show when={!j.enabled}><span class="muted"> · paused</span></Show></td>
                    <td class="mono">{connName(j.config?.connectionId)}<Show when={j.config?.database}> · {j.config.database}</Show></td>
                    <td>{(j.config?.checks || []).length}</td>
                    <td class="muted">{j.scheduleText}</td>
                    <td>
                      <span class="aw-status">{s.text}</span>
                      <Show when={j.latestRun?.finishedAt}><span class="muted aw-when"> · {relTime(j.latestRun.finishedAt)}</span></Show>
                    </td>
                    <td class="aw-row-actions">
                      <button class="btn btn-ghost btn-sm" title="Run now" onClick={() => runNow(j)}>▶</button>
                      <button class="btn btn-ghost btn-sm" title={j.enabled ? 'Pause' : 'Resume'} onClick={() => toggleEnabled(j)}>{j.enabled ? '⏸' : '⏵'}</button>
                      <button class="btn btn-ghost btn-sm" onClick={() => openEdit(j)}>Edit</button>
                      <button class="btn btn-ghost btn-sm" title="Delete" onClick={() => remove(j)}>🗑</button>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </Show>

      <Show when={ed.open}>
        <Modal open={true} onClose={closeEd} title={ed.isNew ? 'New watcher' : 'Edit watcher'} wide>
          <div class="aw-editor">
            <div class="aw-grid2">
              <label class="cr-field"><span class="cr-field-label">Name</span>
                <input class="input" value={input.name} onInput={(e) => setInput('name', e.currentTarget.value)} placeholder="e.g. Orders health" /></label>
              <label class="cr-field"><span class="cr-field-label">Enabled</span>
                <label class="aw-toggle"><input type="checkbox" checked={input.enabled} onInput={(e) => setInput('enabled', e.currentTarget.checked)} /> active</label></label>
              <label class="cr-field"><span class="cr-field-label">Connection</span>
                <select class="input" value={input.connectionId} onChange={(e) => onConn(e.currentTarget.value)}>
                  <option value="">— connection —</option>
                  <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                </select></label>
              <label class="cr-field"><span class="cr-field-label">Database</span>
                <select class="input" value={input.database} onChange={(e) => setInput('database', e.currentTarget.value)} disabled={!dbList()}>
                  <option value="">(none — qualify tables in SQL)</option>
                  <For each={dbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
                </select></label>
            </div>

            {/* Schedule */}
            <div class="aw-sub-title">Schedule</div>
            <div class="aw-sched">
              <select class="input" value={input.schedule.kind} onChange={(e) => setInput('schedule', 'kind', e.currentTarget.value)}>
                <option value="interval">Every…</option><option value="daily">Daily</option>
                <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
              <Show when={input.schedule.kind === 'interval'}>
                <input class="input settings-num" type="number" min="1" value={input.schedule.everyN} onInput={(e) => setInput('schedule', 'everyN', e.currentTarget.value)} />
                <select class="input" value={input.schedule.everyUnit} onChange={(e) => setInput('schedule', 'everyUnit', e.currentTarget.value)}>
                  <option value="min">minutes</option><option value="hour">hours</option>
                </select>
              </Show>
              <Show when={input.schedule.kind !== 'interval'}>
                <input class="input" type="time" value={input.schedule.time} onInput={(e) => setInput('schedule', 'time', e.currentTarget.value)} />
              </Show>
              <Show when={input.schedule.kind === 'weekly'}>
                <div class="aw-days">
                  <For each={WEEKDAYS}>{(d, i) => (
                    <button type="button" class="day-chip" classList={{ on: input.schedule.days.includes(i()) }}
                            onClick={() => setInput('schedule', 'days', (ds) => ds.includes(i()) ? ds.filter((x) => x !== i()) : [...ds, i()])}>{d}</button>
                  )}</For>
                </div>
              </Show>
              <Show when={input.schedule.kind === 'monthly'}>
                <label class="muted aw-dom">day <input class="input settings-num" type="number" min="1" max="28" value={input.schedule.dom} onInput={(e) => setInput('schedule', 'dom', e.currentTarget.value)} /></label>
              </Show>
            </div>

            {/* Alerting */}
            <div class="aw-sub-title">Alert</div>
            <div class="aw-grid2">
              <label class="cr-field"><span class="cr-field-label">Notify target</span>
                <select class="input" value={input.notifyTargetId} onChange={(e) => setInput('notifyTargetId', e.currentTarget.value)}>
                  <option value="0">— none —</option>
                  <For each={targets() ?? []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
                </select></label>
              <label class="cr-field"><span class="cr-field-label">When</span>
                <select class="input" value={input.notifyOn} onChange={(e) => setInput('notifyOn', e.currentTarget.value)}>
                  <option value="failure">on anomaly (recommended)</option>
                  <option value="always">every run</option>
                  <option value="success">only clean runs</option>
                </select></label>
            </div>

            {/* Checks */}
            <div class="aw-sub-title">Checks <button class="btn btn-ghost btn-sm aw-addcheck" onClick={addCheck}>＋ Add check</button></div>
            <div class="aw-checks">
              <For each={input.checks}>
                {(c, i) => (
                  <div class="aw-check">
                    <div class="aw-check-head">
                      <input class="input aw-check-name" value={c.name} onInput={(e) => setCheck(i(), 'name', e.currentTarget.value)} placeholder="check name" />
                      <select class="input aw-check-tpl" value={c.template} onChange={(e) => setCheck(i(), 'template', e.currentTarget.value)}>
                        <For each={TEMPLATES}>{(t) => <option value={t.id}>{t.label}</option>}</For>
                      </select>
                      <button class="rs-chip-x" title="Remove check" onClick={() => removeCheck(i())}>×</button>
                    </div>
                    <Show when={c.template !== 'custom'}>
                      <div class="aw-tpl-fields">
                        <Show when={tplNeeds(i(), 'table')}><input class="input" placeholder="table" value={c.table} onInput={(e) => setCheck(i(), 'table', e.currentTarget.value)} /></Show>
                        <Show when={tplNeeds(i(), 'column')}><input class="input" placeholder="column" value={c.column} onInput={(e) => setCheck(i(), 'column', e.currentTarget.value)} /></Show>
                        <Show when={tplNeeds(i(), 'lo')}><input class="input settings-num" type="number" placeholder="min" value={c.lo} onInput={(e) => setCheck(i(), 'lo', e.currentTarget.value)} /></Show>
                        <Show when={tplNeeds(i(), 'hi')}><input class="input settings-num" type="number" placeholder="max" value={c.hi} onInput={(e) => setCheck(i(), 'hi', e.currentTarget.value)} /></Show>
                      </div>
                    </Show>
                    <textarea class="input mono aw-check-sql" rows="2" value={c.sql} onInput={(e) => setCheck(i(), 'sql', e.currentTarget.value)} placeholder="SELECT <number> …" />
                    <div class="aw-check-cond">
                      <span class="muted">alert when value</span>
                      <select class="input" value={c.operator} onChange={(e) => setCheck(i(), 'operator', e.currentTarget.value)}>
                        <For each={OPERATORS}>{(o) => <option value={o.op}>{o.label}</option>}</For>
                      </select>
                      <input class="input settings-num" type="number" value={c.threshold} onInput={(e) => setCheck(i(), 'threshold', e.currentTarget.value)} />
                      <span class="muted">{isRelativeOp(c.operator) ? '%' : ''}</span>
                      <button class="btn btn-ghost btn-sm" onClick={() => testCheck(i())} disabled={!input.connectionId || !c.sql.trim()}>Test</button>
                      <Show when={c.testVal !== null}><span class="aw-test-ok">= {c.testVal}</span></Show>
                      <Show when={c.testErr}><span class="aw-test-err" title={c.testErr}>✗ {c.testErr}</span></Show>
                    </div>
                  </div>
                )}
              </For>
            </div>

            <div class="modal-actions" style="margin-top:14px;">
              <button class="btn btn-ghost" onClick={closeEd} disabled={ed.saving}>Cancel</button>
              <button class="btn" onClick={save} disabled={ed.saving}>{ed.saving ? 'Saving…' : (ed.isNew ? 'Create watcher' : 'Save')}</button>
            </div>
          </div>
        </Modal>
      </Show>
    </div>
  );
}
